import { useState, type ReactNode } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, LinearProgress, Link, MenuItem, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import { formatDate, formatDateTime } from "@/dates";
import {
  fetchTeslaRemit, fetchTeslaRemits, postTeslaDeposit, postTeslaLine, replanTeslaRemit, updateTeslaRemit, uploadTeslaRemit,
  type LineFix, type TeslaRemit, type TrLine, type TrStatus,
} from "@/api/tesla";

const usdFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (value: string | number | undefined | null) => usdFormat.format(Number(value ?? 0));
const plural = (count: number | undefined, noun: string) => `${count ?? 0} ${noun}${count === 1 ? "" : "s"}`;

const STATUS_CHIP: Record<TrStatus, { label: string; color: "default" | "info" | "success" | "error" | "warning" }> = {
  planning: { label: "Planning…", color: "info" },
  planned: { label: "Dry run", color: "info" },
  posting: { label: "Posting…", color: "warning" },
  partly_posted: { label: "Partly posted", color: "info" },
  posted: { label: "Posted", color: "success" },
  already_booked: { label: "Already booked", color: "default" },
  failed: { label: "Failed", color: "error" },
};

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}

function netsuiteLink(base: string | null | undefined, id: number | null | undefined, label: ReactNode) {
  if (!base || !id) return label;
  return (
    <Link href={`${base}/app/accounting/transactions/transaction.nl?id=${id}`} target="_blank" rel="noopener noreferrer">
      {label}
    </Link>
  );
}

// What posting a line will write, in AR's words.
function planSummary(line: TrLine): string {
  const types = line.records.map((r) => r.type);
  if (types.includes("sales_order")) return "New sales order, invoice and payment";
  if (types.includes("invoice")) return "Invoice the sales order, then payment";
  if (types.includes("payment")) return "Payment on the invoice";
  return "";
}

// ---------------------------------------------------------------------------
// /tesla: upload + recent remits
// ---------------------------------------------------------------------------

export function TeslaRemitsPage() {
  const navigate = useNavigate();
  const remits = useQuery({ queryKey: ["tesla_remits"], queryFn: fetchTeslaRemits });
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState("");
  const [depositOn, setDepositOn] = useState("");

  const upload = useMutation({
    mutationFn: () => uploadTeslaRemit(file as File, reference.trim(), depositOn),
    onSuccess: (created) => navigate(`/tesla/${created.id}`),
  });

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Tesla RMA payments</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Export one remittance from Tesla&apos;s portal (Remittances, <strong>Export to Excel</strong>) and enter its
        <strong> Reference</strong> (e.g. WDVP1983041). You get a dry run: for each SER line, the payment on its Tesla
        invoice, or a new SER-xxxxx-V sales order and invoice when there isn&apos;t one yet, and the bank deposit. Short
        pays stay open on the invoice. Then post one line at a time and create the deposit last. Nothing is written until
        you post a line.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            {file ? file.name : "Choose export"}
            <input hidden type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <TextField label="Reference" size="small" value={reference} placeholder="WDVP1983041"
            onChange={(e) => setReference(e.target.value)} />
          <TextField label="Bank deposit date" type="date" size="small" value={depositOn} helperText="Optional: defaults to the payment date"
            onChange={(e) => setDepositOn(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" disabled={!file || !reference.trim() || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? "Uploading…" : "Upload and dry run"}
          </Button>
        </Stack>
        {upload.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(upload.error)}</Alert>}
      </Paper>

      <Typography variant="h6" gutterBottom>Recent uploads</Typography>
      {remits.isLoading ? (
        <Skeleton variant="rounded" height={120} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Uploaded</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(remits.data ?? []).map((r) => (
                <TableRow key={r.id} hover component={RouterLink} to={`/tesla/${r.id}`} sx={{ textDecoration: "none" }}>
                  <TableCell>{formatDateTime(r.created_at)}</TableCell>
                  <TableCell>{r.reference}</TableCell>
                  <TableCell>{r.payment_on ? formatDate(r.payment_on) : ""}</TableCell>
                  <TableCell align="right">{r.row_count}</TableCell>
                  <TableCell align="right">{usd(r.expected_total)}</TableCell>
                  <TableCell>{STATUS_CHIP[r.status]?.label ?? r.status}</TableCell>
                  <TableCell>{r.created_by}</TableCell>
                </TableRow>
              ))}
              {remits.data?.length === 0 && (
                <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No uploads yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// /tesla/:id: the dry run, then posting line by line
// ---------------------------------------------------------------------------

export function TeslaRemitPage() {
  const id = Number(useParams().id);
  const { user } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["tesla_remit", id],
    queryFn: () => fetchTeslaRemit(id),
    // Poll while planning or while a line or the deposit is posting.
    refetchInterval: (q) => (q.state.data && ["planning", "posting"].includes(q.state.data.status) ? 2000 : false),
  });
  const remit = query.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["tesla_remit", id] });

  const [depositOn, setDepositOn] = useState<string | null>(null);
  const [confirmingDeposit, setConfirmingDeposit] = useState(false);
  const update = useMutation({ mutationFn: (input: { deposit_on?: string; line?: LineFix }) => updateTeslaRemit(id, input), onSuccess: refresh });
  const replan = useMutation({ mutationFn: () => replanTeslaRemit(id), onSuccess: refresh });
  const postLine = useMutation({ mutationFn: (line: number) => postTeslaLine(id, line), onSuccess: refresh });
  const postDeposit = useMutation({ mutationFn: () => postTeslaDeposit(id), onSuccess: () => { setConfirmingDeposit(false); refresh(); } });

  if (!remit) {
    return <AppLayout maxWidth="xl"><Skeleton variant="rounded" height={240} /></AppLayout>;
  }

  const plan = remit.plan ?? {};
  const lines = plan.lines ?? [];
  const state = (l: TrLine) => remit.line_states?.[String(l.line)] ?? {};
  const bookedElsewhere = (l: TrLine) => l.already_booked && state(l).status !== "posted";
  const active = lines.filter((l) => !bookedElsewhere(l));
  const booked = lines.filter(bookedElsewhere);
  const busy = update.isPending || replan.isPending || postLine.isPending;
  const editable = !["posted", "posting", "planning"].includes(remit.status);
  const canPost = !!user?.lightreach_ar_poster && !!remit.write_enabled;
  const postedCount = lines.filter((l) => state(l).status === "posted").length;
  const blockingCount = active.filter((l) => l.blocked && state(l).status !== "posted").length;
  const postedPayments = lines.flatMap((l) => l.records).filter((r) => r.type === "payment" && remit.posted?.[r.external_id]);
  const depositAmount = postedPayments.reduce((sum, r) => sum + Number(r.amount), 0);
  const status = STATUS_CHIP[remit.status];
  const dateValue = depositOn ?? remit.deposit_on ?? "";

  return (
    <AppLayout maxWidth="xl">
      <Button component={RouterLink} to="/tesla" size="small" sx={{ mb: 1 }}>← All uploads</Button>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">Tesla RMA payments: {remit.reference}</Typography>
          <Typography variant="body2" color="text.secondary">
            {remit.payment_on && <>Paid {formatDate(remit.payment_on)} · </>}
            {plural(remit.row_count, "line")} · {remit.filename} · uploaded by {remit.created_by} on {formatDateTime(remit.created_at)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {plan.reconciled !== undefined && (
            <Chip size="small" color={plan.reconciled ? "success" : "warning"} label={plan.reconciled ? "Ties to the remit" : "Doesn't tie"} />
          )}
          {postedCount > 0 && <Chip size="small" color="success" variant="outlined" label={`${postedCount} of ${lines.length} posted`} />}
          {blockingCount > 0 && editable && <Chip size="small" color="error" label={`${blockingCount} to fix`} />}
          <Chip size="small" color={status.color} label={status.label} />
        </Stack>
      </Stack>

      {remit.status === "planning" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reading the export, looking up projects in Sunrise and invoices in NetSuite…
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}
      {remit.error_message && <Alert severity="error" sx={{ mb: 2 }}>{remit.error_message}</Alert>}
      {remit.posted_elsewhere && <Alert severity="warning" sx={{ mb: 2 }}>This remittance ({remit.reference}) was already posted from another upload.</Alert>}
      {remit.status !== "planning" && !remit.write_enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>Dry run only: NetSuite writes are turned off for Tesla remits.</Alert>
      )}
      {remit.write_enabled && !user?.lightreach_ar_poster && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can review this dry run, but posting needs the <strong>Lightreach AR poster</strong> permission (Users page).
        </Alert>
      )}

      {lines.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }}>
              <Typography variant="body2">
                Tesla paid <strong>{usd(plan.expected_total)}</strong> · this tool posts <strong>{usd(plan.deposit_total)}</strong>
                {Number(plan.booked_elsewhere_total) > 0 && <> · booked outside it <strong>{usd(plan.booked_elsewhere_total)}</strong></>}
                {Number(plan.short_total) > 0 && <> · short pays left open <strong>{usd(plan.short_total)}</strong></>}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <TextField label="Bank deposit date" type="date" size="small" value={dateValue}
                disabled={!editable || remit.date_locked || busy}
                helperText={remit.date_locked ? "Locked: records are already in NetSuite on this date" : "Every record is dated this day"}
                onChange={(e) => setDepositOn(e.target.value)} InputLabelProps={{ shrink: true }} />
              {depositOn && depositOn !== remit.deposit_on && (
                <Button size="small" variant="outlined" disabled={busy}
                  onClick={() => update.mutate({ deposit_on: depositOn }, { onSuccess: () => setDepositOn(null) })}>Save date</Button>
              )}
              <Button size="small" startIcon={<RefreshIcon />} disabled={!editable || busy} onClick={() => replan.mutate()}>
                {replan.isPending ? "Re-checking…" : "Re-check NetSuite"}
              </Button>
            </Stack>
          </Paper>
          {(update.isError || replan.isError || postLine.isError) && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(update.error ?? replan.error ?? postLine.error)}</Alert>
          )}

          <LinesTable lines={active} remit={remit} editable={editable} canPost={canPost} busy={busy}
            onFix={(fix) => update.mutate({ line: fix })} onPost={(line) => postLine.mutate(line)} />

          {booked.length > 0 && (
            <Accordion disableGutters variant="outlined" sx={{ mt: 1.5, bgcolor: (t) => alpha(t.palette.text.primary, 0.02) }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  Already booked in NetSuite ({booked.length}) · nothing to post
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <LinesTable lines={booked} remit={remit} editable={false} canPost={false} busy={busy} onFix={() => undefined} onPost={() => undefined} />
              </AccordionDetails>
            </Accordion>
          )}

          <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={700}>Deposit (last step)</Typography>
                {remit.status === "posted" ? (
                  <Typography variant="body2">
                    Created by {remit.posted_by} on {formatDateTime(remit.posted_at)} ·{" "}
                    {netsuiteLink(remit.netsuite_base_url, remit.netsuite_deposit_id, "Open the deposit in NetSuite")}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {plural(postedPayments.length, "posted payment")} totaling <strong>{usd(depositAmount)}</strong>, dated{" "}
                    {remit.deposit_on ? formatDate(remit.deposit_on) : ""}, to 10810 TX Capital Deposit. Unlocks once every line is
                    posted or booked by hand.
                  </Typography>
                )}
                {Number(plan.booked_elsewhere_total) > 0 && remit.status !== "posted" && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {usd(plan.booked_elsewhere_total)} was booked outside this tool; add those payments to this deposit in NetSuite
                    so it matches the bank.
                  </Typography>
                )}
              </Box>
              {remit.status !== "posted" && (
                <Button variant="contained" startIcon={<SendIcon />} disabled={!canPost || !remit.deposit_ready || busy}
                  onClick={() => setConfirmingDeposit(true)}>
                  Create deposit
                </Button>
              )}
            </Stack>
          </Paper>
        </>
      )}

      <Dialog open={confirmingDeposit} onClose={() => setConfirmingDeposit(false)}>
        <DialogTitle>Create the deposit in NetSuite?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {plural(postedPayments.length, "payment")} totaling <strong>{usd(depositAmount)}</strong> go on one deposit to 10810 TX
            Capital Deposit, memo &quot;Tesla RMA Payments {remit.reference}&quot;.
          </Typography>
          {postDeposit.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postDeposit.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDeposit(false)}>Cancel</Button>
          <Button variant="contained" disabled={postDeposit.isPending} onClick={() => postDeposit.mutate()}>
            {postDeposit.isPending ? "Creating…" : "Create deposit"}
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}

function LinesTable({ lines, remit, editable, canPost, busy, onFix, onPost }: {
  lines: TrLine[]; remit: TeslaRemit; editable: boolean; canPost: boolean; busy: boolean;
  onFix: (fix: LineFix) => void; onPost: (line: number) => void;
}) {
  if (lines.length === 0) return null;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Project</TableCell>
            <TableCell align="right">Billed</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell>In NetSuite</TableCell>
            <TableCell>Will post</TableCell>
            <TableCell align="right">Post</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((l) => (
            <LineRow key={l.line} line={l} remit={remit} editable={editable} canPost={canPost} busy={busy} onFix={onFix} onPost={onPost} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LineRow({ line, remit, editable, canPost, busy, onFix, onPost }: {
  line: TrLine; remit: TeslaRemit; editable: boolean; canPost: boolean; busy: boolean;
  onFix: (fix: LineFix) => void; onPost: (line: number) => void;
}) {
  const base = remit.netsuite_base_url;
  const state = remit.line_states?.[String(line.line)] ?? {};
  const posted = state.status === "posted";
  const id = (key: string) => {
    const record = line.records.find((r) => r.key === key);
    return record ? remit.posted?.[record.external_id] : undefined;
  };
  const [projectRef, setProjectRef] = useState("");
  const needsProject = line.flags.some((f) => ["unmatched_project", "unknown_project"].includes(f.code));
  const needsLocation = line.flags.some((f) => f.code === "no_location");

  return (
    <TableRow sx={{ verticalAlign: "top", bgcolor: (t) => (posted ? alpha(t.palette.success.main, 0.04) : undefined) }}>
      <TableCell>{line.line}</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>{line.ref ?? line.invoice_number}</Typography>
        {line.last_name && <Typography variant="caption" color="text.secondary">{line.last_name}</Typography>}
      </TableCell>
      <TableCell align="right">{line.billed ? usd(line.billed) : ""}</TableCell>
      <TableCell align="right">{usd(line.paid)}</TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          {line.sales_order && <Typography variant="body2">{netsuiteLink(base, line.sales_order.id, line.sales_order.tranid)}</Typography>}
          {line.invoice && (
            <Typography variant="body2">
              Invoice {netsuiteLink(base, line.invoice.id, line.invoice.tranid)} · open {usd(line.invoice.unpaid)}
            </Typography>
          )}
          {line.booked_payment && (
            <Typography variant="body2">Payment {netsuiteLink(base, line.booked_payment.id, line.booked_payment.tranid)}</Typography>
          )}
          {!line.sales_order && !line.invoice && line.ref && <Typography variant="body2" color="text.secondary">Nothing yet</Typography>}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack spacing={0.75}>
          {line.records.length > 0 && <Typography variant="body2">{planSummary(line)}</Typography>}
          {posted && (
            <Typography variant="caption">
              Posted: {netsuiteLink(base, id(`${line.line}-so`), "sales order")} ·{" "}
              {netsuiteLink(base, id(`${line.line}-inv`) ?? line.invoice?.id, "invoice")} · {netsuiteLink(base, id(`${line.line}-pmt`), "payment")}
            </Typography>
          )}
          {line.flags.map((f) => (
            <Alert key={f.code} severity={f.blocking ? "error" : f.code === "short_pay" ? "warning" : "info"} sx={{ py: 0 }}>{f.message}</Alert>
          ))}
          {state.error && <Alert severity="error" sx={{ py: 0 }}>{state.error}</Alert>}
          {editable && line.blocked && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {needsProject && (
                <>
                  <TextField size="small" label="Project" placeholder="SER-10046" value={projectRef} onChange={(e) => setProjectRef(e.target.value)} />
                  <Button size="small" variant="outlined" disabled={!projectRef.trim() || busy}
                    onClick={() => onFix({ line: String(line.line), project_ref: projectRef.trim() })}>Use</Button>
                </>
              )}
              {needsLocation && (
                <TextField select size="small" label="Location" value="" sx={{ minWidth: 160 }}
                  onChange={(e) => onFix({ line: String(line.line), location_id: Number(e.target.value) })}>
                  {Object.entries(remit.locations ?? {}).map(([locId, name]) => <MenuItem key={locId} value={locId}>{name}</MenuItem>)}
                </TextField>
              )}
              <Button size="small" disabled={busy} onClick={() => onFix({ line: String(line.line), booked_by_hand: true })}>
                I booked it by hand
              </Button>
            </Stack>
          )}
          {editable && line.booked_by_hand && (
            <Button size="small" sx={{ alignSelf: "flex-start" }} disabled={busy}
              onClick={() => onFix({ line: String(line.line), booked_by_hand: false })}>Undo booked by hand</Button>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        {posted ? (
          <Chip size="small" color="success" label="Posted" />
        ) : state.status === "posting" ? (
          <Chip size="small" color="warning" label="Posting…" />
        ) : remit.postable?.[String(line.line)] ? (
          <Button size="small" variant="contained" disabled={!canPost || busy} onClick={() => onPost(line.line)}>
            {state.status === "failed" ? "Retry" : "Post"}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
